import React from "react";

interface ErrorBoundaryState {
  hasError: boolean;
  error?: Error;
}

interface ErrorBoundaryProps {
  children: React.ReactNode;
  fallback?: React.ComponentType<{ error: Error }>;
}

class ErrorBoundary extends React.Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error("Error caught by boundary:", error, errorInfo);
    
    // Check for chunk loading error which typically happens when assets change after a new build deployment
    if (
      error &&
      (error.name === "ChunkLoadError" ||
        error.message?.includes("ChunkLoadError") ||
        error.message?.includes("Loading chunk") ||
        error.message?.includes("Failed to fetch dynamically imported module"))
    ) {
      const lastReloaded = sessionStorage.getItem("last-chunk-reload");
      const now = Date.now();
      
      // Prevent infinite reloading loops by ensuring at least 10s has passed since last chunk-load reload
      if (!lastReloaded || now - Number(lastReloaded) > 10000) {
        sessionStorage.setItem("last-chunk-reload", String(now));
        console.log("Chunk load error detected, triggering auto-reload to fetch latest code bundles.");
        window.location.reload();
      }
    }
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return <this.props.fallback error={this.state.error!} />;
      }

      return (
        <div
          style={{
            padding: "40px 20px",
            textAlign: "center",
            fontFamily: "Inter, sans-serif",
            maxWidth: "600px",
            margin: "100px auto",
            backgroundColor: "#fff",
            borderRadius: "12px",
            boxShadow: "0 4px 20px rgba(0, 0, 0, 0.08)",
            border: "1px solid #f0f0f0",
          }}
        >
          <div style={{ fontSize: "64px", marginBottom: "20px" }}>⚠️</div>
          <h2 style={{ fontSize: "24px", fontWeight: 600, color: "#333", marginBottom: "12px" }}>
            Something went wrong
          </h2>
          <p style={{ color: "#666", fontSize: "16px", lineHeight: "1.6", marginBottom: "24px" }}>
            The application encountered a rendering issue or failed to load a page chunk. 
            Please refresh the page to reload the newest assets.
          </p>
          <div style={{ display: "flex", justifyContent: "center", gap: "12px" }}>
            <button
              onClick={() => {
                this.setState({ hasError: false });
                window.location.reload();
              }}
              style={{
                padding: "10px 24px",
                background: "#0c70bc",
                color: "white",
                border: "none",
                fontWeight: 600,
                borderRadius: "8px",
                cursor: "pointer",
                transition: "background 0.2s",
              }}
              onMouseOver={(e) => (e.currentTarget.style.background = "#09538c")}
              onMouseOut={(e) => (e.currentTarget.style.background = "#0c70bc")}
            >
              Reload Page
            </button>
            <button
              onClick={() => this.setState({ hasError: false })}
              style={{
                padding: "10px 24px",
                background: "#f3f4f6",
                color: "#4b5563",
                border: "1px solid #d1d5db",
                fontWeight: 600,
                borderRadius: "8px",
                cursor: "pointer",
                transition: "background 0.2s",
              }}
              onMouseOver={(e) => (e.currentTarget.style.background = "#e5e7eb")}
              onMouseOut={(e) => (e.currentTarget.style.background = "#f3f4f6")}
            >
              Try Again
            </button>
          </div>
          <details
            style={{
              marginTop: "40px",
              textAlign: "left",
              backgroundColor: "#f9fafb",
              padding: "16px",
              borderRadius: "8px",
              border: "1px solid #e5e7eb",
            }}
          >
            <summary style={{ cursor: "pointer", fontWeight: 500, color: "#4b5563" }}>
              Technical Details
            </summary>
            <pre
              style={{
                marginTop: "12px",
                fontSize: "12px",
                overflowX: "auto",
                whiteSpace: "pre-wrap",
                color: "#ef4444",
                maxHeight: "200px",
              }}
            >
              {this.state.error?.stack || this.state.error?.message}
            </pre>
          </details>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
